import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";

/** Marks the element (the genie avatar) the mobile stage keeps at the centre of the screen. */
export const STAGE_ANCHOR_CLASS = "genie-stage-anchor";

/** The `top` currently rendered, mid-transition included. */
const currentTopOffset = (el: HTMLElement) => parseFloat(getComputedStyle(el).top) || 0;

/**
 * Returns a vertical offset (applied as relative `top`, never a transform, so
 * fixed-position overlays inside the stage keep the viewport as their
 * containing block) that puts the anchor's centre at the centre of the screen,
 * whatever sits above or below it. The offset is clamped to the visible free
 * space inside the container, so the content never slides under the header
 * buttons or the docked resources rail, and is zero whenever the content does
 * not fit (the stage scrolls) or `enabled` is false.
 */
export function useStageAnchorCentering(
  scrollerRef: RefObject<HTMLElement>,
  contentRef: RefObject<HTMLElement>,
  enabled: boolean,
) {
  const [offset, setOffset] = useState(0);
  const frameRef = useRef<number | null>(null);

  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    const content = contentRef.current;
    const anchor = content?.querySelector<HTMLElement>(`.${STAGE_ANCHOR_CLASS}`);
    if (!enabled || !scroller || !content || !anchor) {
      setOffset(0);
      return;
    }

    const scrollerRect = scroller.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();

    // Only the part of the container on screen counts as free space; a clipping
    // ancestor can leave it taller than what is visible.
    const visibleTop = Math.max(scrollerRect.top, 0);
    const visibleBottom = Math.min(scrollerRect.bottom, window.innerHeight);
    const naturalTop = contentRect.top - currentTopOffset(content);
    const topGap = naturalTop - visibleTop;
    const bottomGap = visibleBottom - (naturalTop + contentRect.height);
    if (topGap < 0 || bottomGap < 0) {
      setOffset(0);
      return;
    }

    const anchorCenter = naturalTop + (anchorRect.top - contentRect.top) + anchorRect.height / 2;
    const desired = window.innerHeight / 2 - anchorCenter;
    const next = Math.round(Math.min(Math.max(desired, -topGap), bottomGap));
    setOffset((prev) => (Math.abs(prev - next) < 1 ? prev : next));
  }, [scrollerRef, contentRef, enabled]);

  useLayoutEffect(() => {
    measure();

    const schedule = () => {
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        measure();
      });
    };

    const resizeObserver = new ResizeObserver(schedule);
    const mutationObserver = new MutationObserver(schedule);
    if (scrollerRef.current) resizeObserver.observe(scrollerRef.current);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
      mutationObserver.observe(contentRef.current, { childList: true, subtree: true });
    }
    window.addEventListener("resize", schedule);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", schedule);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [measure, scrollerRef, contentRef]);

  return offset;
}
